// Demo persona persistence + transport, shared (#409, ADR 0030 revision "demo as
// a stage").
//
// While a workspace is in demo, everything below the off-stage bar is the
// fictional company seen as ONE fictional colleague the real user chose — the
// persona. The choice is client-side: it lives in localStorage like the active
// role does, stamped per tenant AND per master user (activeRoleStorage.ts explains
// why an unstamped per-tenant key is a bug on the shared per-tenant origin), and
// every module client puts it on the wire as `X-Demo-Persona: <cast personId>`.
// The module APIs map that to their own synthetic seat, only while their local
// flag says demo — the mask is theirs to refuse, and this file's job is to make
// sure the UI never sends a stale one and always drops one that was refused.
//
// The value is either a cast personId (the persona worn) or the `'none'`
// sentinel: the user deliberately took the mask off (an admin reaching for
// members, billing or ownership — the off-stage concerns the brief keeps real).
// `null` (no stored value) is the third state and it is NOT "unmasked": it means
// "never chose", and the onboarding store answers it by putting the sheet's
// default persona on and reloading once, so a visitor's first demo screen is
// already on stage.
//
// app-kit rather than five copies (ADR 0026): the stamp and the refusal handling
// are safety rules, and a rule enforced by one of five copies is not enforced.

import {
  DEMO_PERSONA_OWNER_PREFIX,
  DEMO_PERSONA_PREFIX,
  readStamped,
  safeGetItem,
  safeRemoveItem,
  writeStamped,
} from './activeRoleStorage';

/** The request header every module API reads the persona from. */
export const DEMO_PERSONA_HEADER = 'X-Demo-Persona';

/** Stored when the user deliberately wears no persona. Never sent. */
export const DEMO_PERSONA_NONE = 'none';

/** The module refused the persona because it is not in demo (any more). */
export const DEMO_PERSONA_NOT_IN_DEMO = 'DEMO_PERSONA_NOT_IN_DEMO';
/** The module refused the persona because it has no seat for it (or the value is malformed). */
export const DEMO_PERSONA_UNKNOWN = 'DEMO_PERSONA_UNKNOWN';

// Only the marker, never a longer prefix (docs/contracts/demo-cast.v3.md § Identity).
// Mirrors the module APIs' own check so a corrupted value never even reaches them.
const DEMO_CAST_PERSON_RE = /^de3ca570-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const valueKey = (tenantSlug: string) => `${DEMO_PERSONA_PREFIX}${tenantSlug}`;
const ownerKey = (tenantSlug: string) => `${DEMO_PERSONA_OWNER_PREFIX}${tenantSlug}`;

/**
 * The persona this user chose for this tenant: a cast personId, `'none'`, or
 * null when there is no choice we can vouch for. Same ownership rules as the
 * active role — a value stamped by another account is dropped, a value the
 * stamp no longer describes is ignored.
 */
export function readDemoPersona(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
): string | null {
  if (!tenantSlug) return null;
  const value = readStamped(valueKey(tenantSlug), ownerKey(tenantSlug), userId);
  if (!value) return null;
  if (value === DEMO_PERSONA_NONE) return DEMO_PERSONA_NONE;
  // A value that is neither the sentinel nor a cast id is corruption, not a
  // choice: treat it as "never chose" rather than put it on the wire.
  return DEMO_CAST_PERSON_RE.test(value) ? value.toLowerCase() : null;
}

export function writeDemoPersona(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
  value: string,
): void {
  if (!tenantSlug || !userId) return;
  if (value !== DEMO_PERSONA_NONE && !DEMO_CAST_PERSON_RE.test(value)) return;
  writeStamped(valueKey(tenantSlug), ownerKey(tenantSlug), userId, value.toLowerCase());
}

export function clearDemoPersona(tenantSlug: string | null | undefined): void {
  if (!tenantSlug) return;
  safeRemoveItem(valueKey(tenantSlug));
  safeRemoveItem(ownerKey(tenantSlug));
}

/**
 * The header value to send, or null. The `'none'` sentinel is a choice to store,
 * not a persona to send — so this is the ONE reader an axios interceptor should
 * use, and it can never leak the sentinel onto the wire.
 */
export function demoPersonaHeaderValue(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
): string | null {
  const value = readDemoPersona(tenantSlug, userId);
  return value && value !== DEMO_PERSONA_NONE ? value : null;
}

/** The refusal code on an axios-shaped error, or null when it is not a persona refusal. */
export function demoPersonaRefusalCode(error: unknown): string | null {
  const e = error as { response?: { status?: number; data?: unknown } } | null;
  if (!e || e.response?.status !== 403) return null;
  const data = e.response.data as { code?: unknown } | null;
  const code = data && typeof data === 'object' ? data.code : null;
  return code === DEMO_PERSONA_NOT_IN_DEMO || code === DEMO_PERSONA_UNKNOWN ? code : null;
}

// A refusal is remembered per tab (sessionStorage: per tab, gone when the tab
// is) and does two things. It throttles the reload — a refusal drops the mask
// and reloads so every view is re-fetched as the real user, but if the reloaded
// page were refused AGAIN there is nothing left to drop, so no second reload in
// quick succession. And it stops the default persona from coming back on its
// own: the onboarding store puts the sheet's default on a visitor who never
// chose, and the default has no seat in every module (Sabine has no Apprentice
// seat), so without this a refused tab would be re-masked by the next status
// fetch, refused again, and cycle. After a refusal the tab stays unmasked until
// the user chooses a persona again from the bar — an explicit choice clears it.
const REFUSAL_PREFIX = 'demoPersona:refused:';
const RELOAD_GUARD_WINDOW_MS = 15_000;

function refusalKey(tenantSlug: string): string {
  return `${REFUSAL_PREFIX}${tenantSlug}`;
}

function readRefusal(tenantSlug: string): number {
  try {
    return Number(sessionStorage.getItem(refusalKey(tenantSlug)) || 0);
  } catch {
    return 0;
  }
}

/** Was a persona refused in this tab for this tenant (and not chosen again since)? */
export function wasDemoPersonaRefused(tenantSlug: string | null | undefined): boolean {
  return !!tenantSlug && readRefusal(tenantSlug) > 0;
}

/** Forget the refusal: the user made a fresh choice, which is allowed to be refused anew. */
export function clearDemoPersonaRefusal(tenantSlug: string | null | undefined): void {
  if (!tenantSlug) return;
  try {
    sessionStorage.removeItem(refusalKey(tenantSlug));
  } catch {
    // ignore
  }
}

function reloadOnce(tenantSlug: string): void {
  if (typeof window === 'undefined') return;
  const last = readRefusal(tenantSlug);
  const now = Date.now();
  try {
    sessionStorage.setItem(refusalKey(tenantSlug), String(now));
  } catch {
    // no sessionStorage: reload anyway — the persona is already cleared, so a
    // second refusal cannot happen from this cause.
  }
  if (now - last < RELOAD_GUARD_WINDOW_MS) return;
  window.location.reload();
}


/**
 * The response-interceptor half. Returns true when `error` was a persona
 * refusal and the mask has been dropped: the stored choice is cleared (the next
 * load makes a fresh one — the default persona if the demo is still on, nothing
 * if it is not) and the page reloads once so no view keeps showing the previous
 * persona's data. The caller still rejects the error; the reload supersedes it.
 */
export function dropDemoPersonaOnRefusal(
  error: unknown,
  tenantSlug: string | null | undefined,
): boolean {
  const code = demoPersonaRefusalCode(error);
  if (!code || !tenantSlug) return false;
  clearDemoPersona(tenantSlug);
  reloadOnce(tenantSlug);
  return true;
}

// Exposed for the store's "is there a stored choice at all" question without
// re-deriving the key names there.
export function hasStoredDemoPersona(tenantSlug: string | null | undefined): boolean {
  return !!tenantSlug && !!safeGetItem(valueKey(tenantSlug));
}
